import React from "react";
import { CountyPage } from "./countyPage";
import { SearchDto } from "../../../assets/utils/lib/searchDto";
import { CountyForm } from "./countyForm";
import { CountyView } from "./countyView";
import { GRID_ACTION_TYPES, GridActionType } from "../../../assets/types";
import { isGridActionType } from "../../../assets/utils/helpers";
import { CountyService } from "./countyService";
import { Constants } from "../../../assets/utils/lib/constants";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { CountyDto } from "../../../services/dto";
import { useQueryClient } from "@tanstack/react-query";
import { LoadingIndicator } from "../../../assets/vfx";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";

interface CountyContainerProps {
  id?: string;
  searchValues?: SearchDto;
}

export const CountyContainer: React.FC<CountyContainerProps> = () => {
  const queryClient = useQueryClient();
  const [data, setData] = React.useState<CountyDto>();
  const [drawerType, setDrawerType] = React.useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );
  const lookupQuery = CountyService.getCountylookupQuery();

  const { showSnackbar } = useSnackbar();
  const countyQuery = CountyService.getCountyQuery(
    data?.id ?? Constants.emptyGuid
  );
  const { mutateAsync: saveCounty } = CountyService.save();
   
  const handleSubmit = async (values: CountyDto) => {
    const isEdit = data && data.id && data.id !== Constants.emptyGuid;
    values.id = isEdit ? data.id : Constants.emptyGuid;
    return await saveCounty(values, {
      onSuccess: () => {
        showSnackbar("County Saved", MessageSeverity.Success);
        countyQuery.refetch();
        queryClient.invalidateQueries({ queryKey: ["countyGridQuery"] });
        closeDrawer();
      },
    });
  };

  const handleCustomEvent = (actionType: string, dataId: string) => {
    if (isGridActionType(actionType)) {
      setDrawerType(actionType);
      setData({ id: dataId } as CountyDto);
      setSearchAction(actionType, dataId);
    }
  };

  const { setSearchAction, clearSearchAction } = useActionFromQuery(handleCustomEvent);
  const closeDrawer = () => {
      setDrawerType(GRID_ACTION_TYPES.HIDDEN);
      clearSearchAction();
  }

  const handleEdit = (data: CountyDto) => {
    setData(data);
    setDrawerType(GRID_ACTION_TYPES.EDIT);
    setSearchAction(GRID_ACTION_TYPES.EDIT, data?.id);
  };

  const { status: deactivatestatus, mutate: deactivateMutate } =
    CountyService.deleteCounty();

  const handleDelete = () => {
    deactivateMutate(data?.id ?? Constants.emptyGuid, {
      onSuccess: () => {
        showSnackbar("County Deleted", MessageSeverity.Success);
        countyQuery.refetch();
        queryClient.invalidateQueries({ queryKey: ["countyGridQuery"] });
        closeDrawer();
      },
    });
  };

  const renderDrawerContent = () => {
    const isDataLoaded = !!countyQuery?.data;

    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW:
        return (
          !!lookupQuery.data && (
            <CountyForm
              show={true}
              stateLookup={lookupQuery.data}
              onHide={closeDrawer}
              onSave={handleSubmit}
            />
          )
        );

      case GRID_ACTION_TYPES.EDIT:
        return isDataLoaded
          ? !!lookupQuery.data && (
              <CountyForm
                stateLookup={lookupQuery.data}
                data={countyQuery.data}
                show={!!lookupQuery.data}
                onHide={closeDrawer}
                onSave={handleSubmit}
              />
            )
          : null;

      case GRID_ACTION_TYPES.VIEW:
        return isDataLoaded
          ? !!lookupQuery.data && (
              <CountyView
                data={countyQuery.data}
                stateLookup={lookupQuery.data}
                show={!!lookupQuery.data}
                onDelete={handleDelete}
                onHide={closeDrawer}
                onEdit={() => handleEdit(countyQuery.data)}
              />
            )
          : null;

      default:
        return null;
    }
  };
  return (
    <div>
      {lookupQuery?.isLoading ? (
        <LoadingIndicator isFullPage={true} />
      ) : (
        <CountyPage
          stateLookup={lookupQuery.data}
          onCustomEvent={handleCustomEvent}
        />
      )}
      {renderDrawerContent()}
    </div>
  );
};
